/**
 * K9: announce a new release once per person (remembered on their profile, so every device agrees), and the
 * full history filterable by category. Mounted only inside the signed-in shell — a guest opening a registration
 * link never sees operator release notes.
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { writeUpdate } from '@/lib/write';
import { Dialog } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Card, PageHeader } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { useToast } from '@/components/ui/Toast';
import { date } from '@/lib/format';
import { cn } from '@/lib/cn';
import { announcement, RELEASES, type ReleaseCategory } from './releases';

const CURRENT = __BUILD_INFO__.version;
const CATEGORY_LABEL: Record<ReleaseCategory, string> = { feature: 'New', fix: 'Fixed', performance: 'Faster', docs: 'Help', security: 'Security', breaking: 'Changed' };

export function WhatsNewAnnouncer({ userId }: { userId: string }) {
  const toast = useToast();
  const nav = useNavigate();
  const done = useRef(false);
  const [dialog, setDialog] = useState<ReturnType<typeof announcement> | null>(null);
  const seen = useQuery({ queryKey: ['last-seen-version', userId], staleTime: Infinity, queryFn: async () => (await supabase.from('user_profiles').select('last_seen_version').eq('id', userId).single()).data?.last_seen_version ?? null });
  const markSeen = () => void writeUpdate(supabase, 'user_profiles', { last_seen_version: CURRENT }, { id: userId });
  useEffect(() => {
    if (done.current || seen.isLoading || seen.isError || seen.data === CURRENT) return;
    done.current = true;
    const a = announcement(seen.data ?? null, CURRENT);
    if (a.kind === 'dialog') { setDialog(a); return; }
    if (a.kind === 'notice') toast.show({ kind: 'info', id: 'whats-new', message: `Updated to ${CURRENT}: ${a.releases[0]!.headline}.`, action: { label: 'What’s new', run: () => nav('/whats-new') } });
    markSeen(); // a notice, silence or a first visit all count as seen
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once, when the profile's last-seen version arrives
  }, [seen.isLoading, seen.isError, seen.data]);
  if (!dialog) return null;
  const close = () => { markSeen(); setDialog(null); };
  return (
    <Dialog open onClose={close} title={`What’s new in ${CURRENT}`} description={dialog.releases[0]?.headline}
      footer={<><Link to="/whats-new" onClick={close} className="self-center text-sm text-ink-2 underline decoration-brand decoration-2 underline-offset-4">Full history</Link><Button variant="primary" onClick={close}>Got it</Button></>}>
      <ul className="flex flex-col gap-2 text-sm" data-testid="whats-new-dialog">
        {dialog.releases.flatMap((r) => r.notes).map((n, i) => <li key={i} className="flex gap-2"><Badge tone={n.category === 'security' ? 'warning' : n.category === 'fix' ? 'success' : 'info'}>{CATEGORY_LABEL[n.category]}</Badge><span className="text-ink-2">{n.text}</span></li>)}
      </ul>
    </Dialog>
  );
}

export default function WhatsNewPage() {
  const [cat, setCat] = useState<ReleaseCategory | 'all'>('all');
  const cats = Object.keys(CATEGORY_LABEL) as ReleaseCategory[];
  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader title="What’s new" description={`You’re on version ${CURRENT}. Every change, newest first.`} />
      <div role="tablist" aria-label="Filter by kind of change" className="mb-4 flex flex-wrap gap-1.5">
        {(['all', ...cats] as const).map((c) => (
          <button key={c} role="tab" aria-selected={cat === c} onClick={() => setCat(c)} className={cn('rounded-chip px-2.5 py-1 text-[13px] ring-1 ring-line', cat === c ? 'bg-surface font-semibold text-ink ring-line-strong' : 'text-ink-2 hover:text-ink')}>{c === 'all' ? 'Everything' : CATEGORY_LABEL[c]}</button>
        ))}
      </div>
      <div className="flex flex-col gap-4" data-testid="release-history">
        {RELEASES.map((r) => {
          const notes = r.notes.filter((n) => cat === 'all' || n.category === cat);
          if (!notes.length) return null;
          return (
            <Card key={r.version} className="p-5" data-release={r.version}>
              <h2 className="text-[15px] font-semibold text-ink">{r.version} <span className="font-normal text-muted">· {date(r.date)}</span></h2>
              <p className="mt-0.5 text-sm text-ink-2">{r.headline}</p>
              <ul className="mt-3 flex flex-col gap-1.5 text-sm">
                {notes.map((n, i) => <li key={i} className="flex gap-2"><Badge tone="neutral">{CATEGORY_LABEL[n.category]}</Badge><span className="text-ink-2">{n.text}</span></li>)}
              </ul>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
